import { Component, createEffect, JSX, ParentProps, splitProps, ValidComponent } from "solid-js";
import { Dynamic } from "solid-js/web";
import { requireDropdownContext, TriggerAction } from "common";


export type ComponentProps<T> = T extends Component<infer P> ?
    P : T extends keyof JSX.IntrinsicElements
        ? JSX.IntrinsicElements[T] : Record<string, unknown>

export type DropdownTriggerProps<T extends ValidComponent = 'div'> = {
    as?: T
    action?: TriggerAction
    onClick?: (event: MouseEvent) => void,
} & ComponentProps<T>


export function DropdownTrigger<T extends ValidComponent = 'div'>(props: ParentProps<DropdownTriggerProps<T>>) {
    const [local, other] = splitProps(props, ['as', 'action', 'onClick'])
    const ddContext = requireDropdownContext()

    createEffect(() => {
        ddContext.setTriggerAction(local.action ?? 'click')
    })

    const onClick = (e: MouseEvent) => {
        ddContext.toggle()
        props.onClick?.(e)
    }

    const onKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            ddContext.toggle()
        }
    }

    const initRef = (el: HTMLElement) => {
        ddContext.registerTrigger(el)
    }

    return (
        <Dynamic
            component={local.as ?? 'div'}
            ref={initRef}
            onClick={onClick}
            onMouseEnter={() => ddContext.setIsHoveringTrigger(true)}
            onMouseLeave={() => ddContext.setIsHoveringTrigger(false)}
            onKeyDown={onKeydown}
            tabIndex={0}
            role='button'
            {...other}
        >
            {props.children}
        </Dynamic>
    )
}